import { Fragment } from 'react';

import {
    Box,
    Icon,
    IconName,
    Popover,
    PopoverDirection,
    Spacer,
    SpacerSize,
    Text,
    TextLevels,
    Title,
    TitleLevels,
    TitleMarkup,
    TypographyAlign,
} from '@bytel/trilogy-react-ts';

import { PRICE_TYPE } from '@app-types/price';
import { CartType } from '@app-types/product';

import { BoxInfo } from '@components/box-info';
import { ListItemWithPrice } from '@components/list-item-with-price';
import { OptionsList } from '@components/options-list';

import { hasPriceTypeProduct } from '@helpers/cart';
import { shouldShowCents } from '@helpers/utils';

import PlanPlusOptionHeader from './plan-plus-option-header';

export type CartContentProps = {
    cart: CartType | null;
};

export function CartContent({ cart }: Readonly<CartContentProps>) {
    if (!cart) return null;
    const isSim = cart?.sim?.esim === false;

    return (
        <>
            <BoxInfo header={<PlanPlusOptionHeader total={cart.total} />}>
                <Title marginless level={TitleLevels.TWO}>
                    Votre forfait sans engagement
                </Title>
                <ListItemWithPrice
                    title={cart.product?.name}
                    subTitle=''
                    amount={cart.product?.prices.final || 0}
                    subTitleClassName='has-text-grey-dark has-text-weight-normal'
                    titleClassName=''
                    priceClassName=''
                    period='mois'
                />
                <OptionsList classnames='' updatable={false} cart={cart} />
            </BoxInfo>
            <Spacer size={SpacerSize.LARGE} />
            {hasPriceTypeProduct(cart, PRICE_TYPE.TODAY) ? CartContentByType(cart, isSim, PRICE_TYPE.TODAY) : null}
            <Spacer size={SpacerSize.LARGE} />
            {hasPriceTypeProduct(cart, PRICE_TYPE.DEFERRED)
                ? CartContentByType(cart, isSim, PRICE_TYPE.DEFERRED)
                : null}
            <Spacer size={SpacerSize.LARGE} />
        </>
    );
}

export function CartContentByType(cart: CartType, isSim: boolean, type: PRICE_TYPE, showDefferedBox = false) {
    return (
        <Fragment>
            <BoxInfo header={CartContentHeader(cart, isSim, type)}>
                {cart.sim?.priceType === type && (
                    <Fragment>
                        <Title marginless level={TitleLevels.THREE}>
                            {isSim ? 'Carte SIM triple découpe' : 'eSIM'}
                        </Title>
                        <ListItemWithPrice
                            subTitle={cart.sim?.esim ? 'Une puce dématérialisée.' : 'Une puce physique.'}
                            amount={cart.sim?.price || 0}
                            subTitleClassName='has-text-grey-dark has-text-weight-normal'
                            titleClassName=''
                            priceClassName=''
                            showCents={shouldShowCents(cart.sim?.price)}
                        />
                    </Fragment>
                )}
                {cart.deliveryMode && cart.deliveryMode.priceType === type && (
                    <Fragment>
                        <Title marginless level={TitleLevels.THREE}>
                            Frais de livraison
                        </Title>
                        <ListItemWithPrice
                            subTitle={'Livraison'}
                            amount={cart.deliveryMode?.price || 0}
                            subTitleClassName='has-text-grey-dark has-text-weight-normal'
                            titleClassName=''
                            priceClassName=''
                            showCents={shouldShowCents(cart.deliveryMode?.price)}
                        />
                    </Fragment>
                )}
                {cart.mobileFees && cart.mobileFees.prices.type === type && (
                    <Fragment>
                        <Title level={TitleLevels.THREE} markup={TitleMarkup.H3}>
                            {cart.mobileFees?.name}
                            <Popover
                                content={
                                    'Ces frais couvrent la mise en service et la configuration de votre nouvelle ligne.'
                                }
                                direction={PopoverDirection.BOTTOM}
                            >
                                <Icon name={IconName.INFOS_CIRCLE} />
                            </Popover>
                        </Title>
                        <ListItemWithPrice
                            subTitle={cart.mobileFees?.name}
                            amount={cart.mobileFees?.price || 0}
                            subTitleClassName='has-text-grey-dark has-text-weight-normal'
                            titleClassName=''
                            priceClassName=''
                            showCents={shouldShowCents(cart.mobileFees?.price)}
                        />
                    </Fragment>
                )}
                {showDefferedBox && (
                    <Box className='is-shadowless is-bordered level'>
                        <Text marginless level={TextLevels.TWO} typo={TypographyAlign.TEXT_CENTERED}>
                            Commandez aujourd’hui !<br /> Payez sur votre première facture
                        </Text>
                    </Box>
                )}
            </BoxInfo>
        </Fragment>
    );
}

export function CartContentHeader(cart: CartType, isSim: boolean, type: PRICE_TYPE) {
    const title = type === PRICE_TYPE.TODAY ? 'À régler aujourd’hui' : 'À régler sur la 1ère facture';
    const getPaymentSubtitle = () => {
        let subtitle = '';
        if (cart.sim) {
            const simText = !isSim ? 'eSIM' : 'Carte SIM';
            if (cart.sim.priceType === type) {
                subtitle = simText;
            }
        }

        if (cart.mobileFees) {
            const shouldIncludeMobileFees = cart.mobileFees.prices.type === type;

            if (shouldIncludeMobileFees) {
                subtitle = subtitle ? `${subtitle} + ${cart.mobileFees.name}` : cart.mobileFees.name;
            }
        }

        if (cart.deliveryMode && cart.deliveryMode.price >= 0) {
            const shouldIncludeDelivery = cart.deliveryMode.priceType === type;

            if (shouldIncludeDelivery) {
                subtitle = subtitle ? `${subtitle} + Livraison` : 'Livraison';
            }
        }

        return subtitle || '';
    };

    const getAmount = () => {
        let amount = 0;

        if (cart.sim?.priceType === type) {
            amount += cart.sim.price || 0;
        }
        if (cart.mobileFees?.prices.type === type) {
            amount += cart.mobileFees.price || 0;
        }
        if (cart.deliveryMode?.priceType === type) {
            amount += cart.deliveryMode.price || 0;
        }

        return amount;
    };
    return (
        <ListItemWithPrice
            mainTitle={title}
            subTitle={getPaymentSubtitle()}
            amount={getAmount()}
            subTitleClassName='has-text-weight-normal'
            titleClassName='has-text-white'
            className='has-text-white'
            showCents={shouldShowCents(getAmount())}
        />
    );
}
